/**
 * GoldenBookQueue — the Golden Book Desk (19 Jul 2026).
 * Arrange + membership-upsell requests from /goldenbook. Partner
 * provenance (booking urls, club contacts, dress) is desk-only —
 * read-only staff see the ask, never the plumbing.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["new", "in_progress", "done"];

const MEMBERSHIP_LABEL = {
  has_number: "has number",
  included_in_programme: "in programme",
  needs_membership: "UPSELL · needs one",
};

function pillTone(s) {
  if (s === "in_progress") return "os-p-prog";
  if (s === "done") return "os-p-done";
  return "os-p-new";
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return iso;
  }
}

export default function GoldenBookQueue() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 25, role: "read_only", has_more: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: null, q: "", page: 1 });
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    setError("");
    try {
      const p = new URLSearchParams();
      if (f.status) p.set("status", f.status);
      if ((f.q || "").trim()) p.set("q", f.q.trim());
      p.set("page", String(f.page));
      const r = await api.get(`/os-admin/goldenbook/requests?${p.toString()}`);
      setRows(r.data.rows || []);
      setMeta({ total: r.data.total || 0, page: r.data.page || 1,
                per_page: r.data.per_page || 25, role: r.data.role || "read_only",
                has_more: !!r.data.has_more });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not open the Book's desk.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, [filters]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/os-admin/goldenbook/requests/${id}/status`, { status });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Update failed" });
    }
  };

  const retryZoho = async (id) => {
    setBusy(id);
    setFlash(null);
    try {
      const r = await api.post(`/os-admin/goldenbook/requests/${id}/push-zoho`);
      if (r.data.ok) {
        setRows((prev) => prev.map((x) => (x.id === id
          ? { ...x, zoho_ticket_id: r.data.zoho_ticket_id, zoho_pushed: true } : x)));
        setFlash({ id, ok: true, msg: `Ticket ${r.data.zoho_ticket_id}` });
      } else {
        setFlash({ id, ok: false, msg: r.data.detail || "Push failed" });
      }
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Push failed" });
    } finally {
      setBusy(null);
    }
  };

  const isReadOnly = meta.role === "read_only";

  return (
    <div className="os-fyi-queue" data-testid="os-gbqueue-page">
      <div className="os-pagehead">
        <h1>Golden Book Desk</h1>
        <span className="count">{loading ? "Loading…" : `${meta.total} requests`}</span>
      </div>
      <div className="os-pagesub">
        Arranges + membership upsells from the Book · <code>goldenbook_requests</code> ·
        role: <strong data-testid="os-gbqueue-role">{meta.role}</strong>
      </div>

      <div className="os-fyi-filterblock" data-testid="os-gbqueue-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Status</span>
          <button type="button" className={`os-fchip ${!filters.status ? "on" : ""}`}
                  onClick={() => setFilters((p) => ({ ...p, status: null, page: 1 }))}
                  data-testid="os-gbqueue-filter-all">all</button>
          {STATUSES.map((s) => (
            <button key={s} type="button"
                    className={`os-fchip ${filters.status === s ? "on" : ""}`}
                    onClick={() => setFilters((p) => ({ ...p, status: s, page: 1 }))}
                    data-testid={`os-gbqueue-filter-${s}`}>
              {s.replace("_", " ")}
            </button>
          ))}
          <span className="os-spacer" />
          <input type="search" placeholder="Search entry · member · city"
                 value={filters.q}
                 onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
                 maxLength={80} className="os-fyi-search"
                 data-testid="os-gbqueue-search" />
        </div>
      </div>

      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-gbqueue-error">{error}</div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-gbqueue-empty">
          No Golden Book requests match these filters.
        </div>
      ) : (
        <table className="os-tbl" data-testid="os-gbqueue-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Member</th>
              <th>The entry</th>
              <th>Membership</th>
              {!isReadOnly && <th>Partner details (desk only)</th>}
              <th>Zoho</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`os-gbqueue-row-${r.id}`}>
                <td title={r.created_at}>{relativeTime(r.created_at)}</td>
                <td>
                  <div className="os-fyi-title">{r.member_name || "—"}</div>
                  {!isReadOnly && r.member_phone && (
                    <div className="os-fyi-sub">{r.member_phone}</div>
                  )}
                </td>
                <td>
                  <div className="os-fyi-title" style={{ maxWidth: 220 }}>{r.chosen || "—"}</div>
                  <div className="os-fyi-sub">
                    {[r.kind === "membership_upsell" ? "MEMBERSHIP LEAD" : r.kind,
                      r.city, r.keeper ? `kept by ${r.keeper}` : null]
                      .filter(Boolean).join(" · ")}
                  </div>
                  {r.member_note && <div className="os-fyi-sub">note: {r.member_note}</div>}
                </td>
                <td>
                  {r.kind === "membership_upsell" ? (
                    <span className="os-pill os-p-prog">sales lead</span>
                  ) : r.mc_membership_status ? (
                    <>
                      <span className={`os-pill ${r.mc_membership_status === "needs_membership" ? "os-p-prog" : "os-p-done"}`}
                            data-testid={`os-gbqueue-membership-${r.id}`}>
                        {MEMBERSHIP_LABEL[r.mc_membership_status] || r.mc_membership_status}
                      </span>
                      {!isReadOnly && r.mc_membership_number && (
                        <div className="os-fyi-sub">{r.mc_membership_number}</div>
                      )}
                    </>
                  ) : <span className="os-fyi-dim">—</span>}
                </td>
                {!isReadOnly && (
                  <td>
                    {r.booking_url && (
                      <div className="os-fyi-sub">
                        <a href={r.booking_url} target="_blank" rel="noreferrer"
                           className="os-ref"
                           data-testid={`os-gbqueue-booking-${r.id}`}>booking ↗</a>
                      </div>
                    )}
                    {r.source && (
                      <div className="os-fyi-sub" style={{ maxWidth: 200 }}>
                        <a href={r.source} target="_blank" rel="noreferrer" className="os-ref">site ↗</a>
                      </div>
                    )}
                    {r.club_contact_name && <div className="os-fyi-sub">{r.club_contact_name}</div>}
                    {(r.club_contact_whatsapp || r.source_phone) && (
                      <div className="os-fyi-sub">{r.club_contact_whatsapp || r.source_phone}</div>
                    )}
                    {r.dress && <div className="os-fyi-sub" style={{ maxWidth: 200 }}>dress: {r.dress.slice(0, 60)}</div>}
                    {!r.booking_url && !r.source && !r.club_contact_name && !r.source_phone
                      && <span className="os-fyi-dim">—</span>}
                  </td>
                )}
                <td>
                  {r.routed_to === "mindescapes.club" ? (
                    <span className="os-fyi-dim" title="Tenant embed · ticket lives in MC's Zoho">
                      MC tenant ↗
                    </span>
                  ) : r.zoho_ticket_id ? (
                    <span className="os-pill os-p-done" data-testid={`os-gbqueue-zoho-${r.id}`}>
                      #{r.zoho_ticket_id}
                    </span>
                  ) : r.zoho_skip_reason === "test_traffic" ? (
                    <span className="os-fyi-dim">test · skipped</span>
                  ) : (
                    <button type="button" className="os-btn-ghost"
                            disabled={busy === r.id || isReadOnly}
                            onClick={() => retryZoho(r.id)}
                            data-testid={`os-gbqueue-retry-${r.id}`}>
                      {busy === r.id ? "Pushing…" : "Retry Zoho ↻"}
                    </button>
                  )}
                  {flash && flash.id === r.id && (
                    <div className={flash.ok ? "os-fyi-sub" : "os-fyi-sub os-fyi-msg-err"}
                         data-testid={`os-gbqueue-flash-${r.id}`} style={{ marginTop: 4 }}>
                      {flash.msg}
                    </div>
                  )}
                </td>
                <td>
                  {isReadOnly ? (
                    <span className={`os-pill ${pillTone(r.status)}`}>
                      {(r.status || "new").replace("_", " ")}
                    </span>
                  ) : (
                    <select value={r.status || "new"}
                            onChange={(e) => setStatus(r.id, e.target.value)}
                            className="os-fyi-search" style={{ padding: "4px 8px" }}
                            data-testid={`os-gbqueue-status-${r.id}`}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="os-fyi-pagi" data-testid="os-gbqueue-pagination">
        <button type="button" className="os-btn-ghost"
                disabled={meta.page <= 1 || loading}
                onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                data-testid="os-gbqueue-page-prev">‹ Prev</button>
        <span className="os-fyi-pagemark">Page {meta.page}</span>
        <button type="button" className="os-btn-ghost"
                disabled={!meta.has_more || loading}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
                data-testid="os-gbqueue-page-next">Next ›</button>
      </div>
    </div>
  );
}
